import React from 'react';
import { PetType } from '@spike/model';
import { Radio } from 'components/UI/V3/Forms/Radio';
import { DurationSelector } from './DurationSelector';
import { LatePickupFeeConfig } from '../LatePickup/LatePickupFeeConfig';
import { GracePeriodSection } from '../LatePickup/GracePeriodSection';
import { DaycareFixedPriceTable } from '../PriceTable/DaycareFixedPriceTable';
import { FixedPriceOption, DaycareDurationOption, LatePickupFeeType, DAYCARE_DURATION_OPTIONS } from 'model/Service';
import {
    DAYCARE_DURATION_OPTIONS_WITH_LABELS,
    DurationOptionWithLabel
} from 'components/Daycare/Form/utils/DaycarePricingUtils';
import styles from './ExpandableDurationOptions.module.scss';

interface ExpandableDurationOptionsProps {
    pricingType: 'fixed' | 'by_pet_size';
    selectedDuration: DaycareDurationOption | null;
    duration: string;
    latePickupFee: {
        enabled: boolean;
        type?: LatePickupFeeType;
        gracePeriod: number;
    };
    fixedPrices: FixedPriceOption[];
    selectedPetTypes: Array<PetType>;
    latePickupFees?: Record<string, number>;
    onDurationChange: (duration: DaycareDurationOption) => void;
    onDurationValueChange: (value: string) => void;
    onLatePickupFeeChange: (config: any) => void;
    onFixedPricesChange: (prices: FixedPriceOption[]) => void;
    onLatePickupFeesChange?: (fees: Record<string, number>) => void;
    onPriceValidationChange?: (isValid: boolean, errors: Record<string, string>) => void;
    className?: string;
}

export const ExpandableDurationOptions: React.FC<ExpandableDurationOptionsProps> = ({
    pricingType,
    selectedDuration,
    duration,
    latePickupFee,
    fixedPrices,
    selectedPetTypes,
    latePickupFees = {},
    onDurationChange,
    onDurationValueChange,
    onLatePickupFeeChange,
    onFixedPricesChange,
    onLatePickupFeesChange,
    onPriceValidationChange,
    className
}) => {
    const durationOptions = DAYCARE_DURATION_OPTIONS_WITH_LABELS;

    const renderDurationContent = (durationType: DaycareDurationOption) => {
        if (selectedDuration !== durationType) return null;

        return (
            <div className={styles.durationContent}>
                {/* Horizontal options block for Half Day and Full Day */}
                {durationType !== DAYCARE_DURATION_OPTIONS.HOURLY && (
                    <div className={styles.optionsBlock}>
                        <div className={styles.optionSection}>
                            <DurationSelector
                                value={duration}
                                onChange={onDurationValueChange}
                                label="Select Duration"
                            />
                        </div>

                        {/* Late Pickup Fee */}
                        <div className={styles.optionSection}>
                            <LatePickupFeeConfig
                                durationOption={durationType}
                                latePickupFee={latePickupFee}
                                onLatePickupFeeChange={onLatePickupFeeChange}
                            />
                        </div>

                        {/* Grace Period */}
                        <div className={styles.optionSection}>
                            {latePickupFee.enabled && (
                                <GracePeriodSection
                                    value={latePickupFee.gracePeriod}
                                    onChange={(gracePeriod: number) =>
                                        onLatePickupFeeChange({
                                            ...latePickupFee,
                                            gracePeriod
                                        })
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}

                {/* Pricing Table for Fixed Price + Hourly */}
                {pricingType === 'fixed' && durationType === DAYCARE_DURATION_OPTIONS.HOURLY && (
                    <div className={styles.tableSection}>
                        <DaycareFixedPriceTable
                            selectedPetTypes={selectedPetTypes}
                            durationOption={DAYCARE_DURATION_OPTIONS.HOURLY}
                            fixedPrices={fixedPrices}
                            onFixedPricesChange={onFixedPricesChange}
                            latePickupFees={latePickupFees}
                            onLatePickupFeesChange={onLatePickupFeesChange}
                            onValidationChange={onPriceValidationChange}
                        />
                    </div>
                )}

                {/* Pricing Table for Fixed Price + Half Day/Full Day */}
                {pricingType === 'fixed' &&
                    (durationType === DAYCARE_DURATION_OPTIONS.HALF_DAY ||
                        durationType === DAYCARE_DURATION_OPTIONS.FULL_DAY) && (
                        <div className={styles.tableSection}>
                            <DaycareFixedPriceTable
                                selectedPetTypes={selectedPetTypes}
                                durationOption={durationType}
                                fixedPrices={fixedPrices}
                                onFixedPricesChange={onFixedPricesChange}
                                showLatePickupColumn={latePickupFee.enabled}
                                latePickupFeeType={latePickupFee.type}
                                latePickupFees={latePickupFees}
                                onLatePickupFeesChange={onLatePickupFeesChange}
                                onValidationChange={onPriceValidationChange}
                            />
                        </div>
                    )}

                {/* TODO: Implement variable pricing for by_pet_size */}
                {pricingType === 'by_pet_size' && (
                    <div className={styles.tableSection}>
                        <div className={styles.placeholder}>
                            <p>Variable pricing here</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`${styles.container} ${className || ''}`}>
            {durationOptions.map((option: DurationOptionWithLabel) => (
                <div
                    key={option.key}
                    className={styles.durationOption}
                >
                    <Radio
                        inlineLabel={option.label}
                        checked={selectedDuration === option.value}
                        onChange={() => onDurationChange(option.value)}
                    />
                    {renderDurationContent(option.value)}
                </div>
            ))}
        </div>
    );
};
